import { Box, Text } from "@mantine/core";
import useReportPreviewStyle from "./reportPreviewStyle";

const PER_PAGE = 3;

// Typed ("Type") signatures render in Island Moments — the same face the
// signature module types them in (Settings → My Signature). The base size is
// .reSig's 28px, but the 112px signature slot can't fit a long name at that
// size, so it scales down by length. 334 ≈ the 108px inner slot divided by
// Island Moments' ~0.323em average advance, i.e. the largest size at which
// `len` characters still fit. Mirrors the report PDF, which steps the size down
// against real glyph metrics (doc.widthOfString) the same way.
const typedSigFontSize = (text) => {
  const len = String(text || "").trim().length;
  if (!len) return 28;
  return Math.max(11, Math.min(28, Math.floor(334 / len)));
};

// Flat label/value fields inside a .re-body grid — mirrors the PDF's own
// drawOutcomeRow (half-width plain field) / drawContentSection (full-width,
// boxed) pairing for outcomes/goals/essential-support, matching the demo's
// plain field layout instead of a bordered numbered card.
// fields: [label, value, isFull, isBoxed][]
const FieldRow = ({ classes, fields }) => (
  <>
    {fields
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
      .map(([label, value, isFull, isBoxed]) => (
        <div className={isFull ? `${classes.reF} ${classes.reFFull}` : classes.reF} key={label}>
          <div className={classes.reFK}>{label}</div>
          <div className={isBoxed ? `${classes.reFV} ${classes.reFVBoxed}` : classes.reFV}>{value}</div>
        </div>
      ))}
  </>
);

// Renders the same content the report PDF would contain, as real HTML/CSS
// instead of embedding the PDF file — pixel-matches the notes-redesign demo's
// mock "Report Preview" screen. Used for EVERY note type, Incident Report
// included: incident entries arrive with their incidentDetails already
// flattened into customFields by /reportPreviewData, so they render with the
// identical entry layout rather than as a raw PDF embed.
// Does not resolve NType.custom[] dynamic fields (see /reportPreviewData) —
// custom note types show their plain Description here; the actual downloaded
// PDF still renders custom fields correctly.
const ReportPreviewPane = ({ cover, entries }) => {
  const { classes } = useReportPreviewStyle();

  const Letterhead = () => (
    <div className={classes.letterhead}>
      <div className={classes.brand}>
        {/* Brand mark only when "Include agency letterhead" is on. */}
        {cover.includeLetterhead !== false &&
          (cover.facilityLogo ? (
            <img src={cover.facilityLogo} alt="" className={classes.logoImg} />
          ) : (
            <div className={classes.logo}>{cover.facilityInitials}</div>
          ))}
        <div className={classes.org}>{cover.facilityName}</div>
      </div>
      <div className={classes.lhRight}>
        Generated {cover.generatedOn}
        <br />
        Confidential
      </div>
    </div>
  );

  const pageChunks = [];
  for (let i = 0; i < entries.length; i += PER_PAGE) {
    pageChunks.push(entries.slice(i, i + PER_PAGE));
  }
  const totalPages = 1 + pageChunks.length;

  const EntryBlock = (entry) => {
    const clientLabel =
      entry.clientNames.length > 1 ? `Clients (${entry.clientNames.length})` : "Client";
    const shiftLabel = entry.shift
      ? `${entry.shift.charAt(0).toUpperCase() + entry.shift.slice(1)} shift${entry.time ? ` · ${entry.time}` : ""}`
      : entry.time || "";

    return (
      <div className={classes.reEntry} key={entry.id}>
        <div className={classes.reHead}>
          <div className={classes.reWho}>
            <div className={classes.reK}>{clientLabel}</div>
            <div className={classes.reClient}>{entry.clientNames.join(", ") || "Unknown Client"}</div>
          </div>
          <span className={classes.reChip}>{entry.noteTypeName}</span>
          <div className={classes.reWhen}>
            <div className={classes.reWhenD}>{entry.date}</div>
            {shiftLabel && <div className={classes.reWhenS}>{shiftLabel}</div>}
            {entry.facilityName && <div className={classes.reWhenS}>{entry.facilityName}</div>}
          </div>
        </div>
        <div className={classes.reBody}>
          {entry.outcomes &&
            entry.outcomes.map((o) => (
              <FieldRow
                key={`o-${o.no}`}
                classes={classes}
                fields={[
                  ["Outcome Number", o.no, false],
                  ["Outcome Title", o.title, false],
                  ["Description", o.desc, true, true],
                ]}
              />
            ))}
          {entry.goals &&
            entry.goals.map((g) => (
              <FieldRow
                key={`g-${g.no}`}
                classes={classes}
                fields={[
                  ["Goal Number", g.no, false],
                  ["Outcome/Goal", g.goal, false],
                  ["Behavior", g.behavior, true, true],
                  ["Response", g.response, true, true],
                  ["Plan", g.plan, true, true],
                ]}
              />
            ))}
          {entry.essentialSupport &&
            entry.essentialSupport.map((es, i) => (
              <FieldRow
                key={`es-${i}`}
                classes={classes}
                fields={[
                  ["Essential Number", es.no, false],
                  ["Essential Support Type", es.type, false],
                  ["Frequency", es.freq, false],
                  ["Essential Support Activity", es.activity, true],
                  ["Description", es.desc, true, true],
                ]}
              />
            ))}
          {entry.customFields &&
            entry.customFields.length > 0 &&
            entry.customFields.map((cf) => (
              <div className={`${classes.reF} ${classes.reFFull}`} key={cf.label}>
                <div className={classes.reFK}>{cf.label}</div>
                <div className={classes.reFV}>
                  {cf.isMultiOptions ? (cf.value.length ? cf.value.join(", ") : "—") : cf.value || "—"}
                </div>
              </div>
            ))}
          {entry.description !== null && (
            <div className={`${classes.reF} ${classes.reFFull}`}>
              <div className={classes.reFK}>Description</div>
              <div className={`${classes.reFV} ${classes.reFVBoxed}`}>{entry.description}</div>
            </div>
          )}
          {cover.includeApprovalTrail && (
            <FieldRow
              classes={classes}
              fields={[
                [
                  "Approval Status",
                  entry.approvalStatus
                    ? entry.approvalStatus.charAt(0).toUpperCase() + entry.approvalStatus.slice(1)
                    : "Pending",
                  false,
                ],
                [
                  "Submitted By",
                  entry.submittedDate
                    ? `${entry.submittedBy} · ${entry.submittedDate}`
                    : entry.submittedBy,
                  false,
                ],
                [
                  "Rejection Reason",
                  entry.approvalStatus === "rejected" ? entry.rejectionReason : "",
                  true,
                  true,
                ],
              ]}
            />
          )}
        </div>
        {cover.includeSignatures !== false && (
        <div className={classes.reFoot}>
          {(() => {
            // Prefer the per-staff signature list (each staff shows their OWN
            // signature); fall back to staffNames for older report payloads.
            const signers =
              entry.staffSignatures?.length > 0
                ? entry.staffSignatures
                : entry.staffNames.map((name) => ({
                    name,
                    signatureImage: entry.signatureImage,
                    signatureText: entry.signatureText,
                  }));
            return (
              <>
                <span className={classes.reSiglbl}>Signed by ({signers.length})</span>
                {signers.map(({ name, signatureImage, signatureText }) => (
                  <div className={classes.reSigblk} key={name}>
                    {signatureImage ? (
                      <img src={signatureImage} alt="Signature" className={classes.reSigImg} />
                    ) : signatureText ? (
                      <div
                        className={classes.reSig}
                        style={{ fontSize: typedSigFontSize(signatureText) }}
                      >
                        {signatureText}
                      </div>
                    ) : (
                      // Signer has nothing on file — say so explicitly instead
                      // of leaving an empty slot that reads as an unsigned or
                      // clipped signature. Mirrored in the PDF footer.
                      <div className={classes.reSigMissing}>Signature not found</div>
                    )}
                    <div className={classes.reSigname}>{name}</div>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
        )}
      </div>
    );
  };

  return (
    <Box>
      {/* ── Cover page ──────────────────────────────────────────────────── */}
      <div className={classes.page}>
        <Letterhead />
        <div className={classes.coverMid}>
          {/* Report title is optional — render it only when one was entered. */}
          {cover.reportTitle && (
            <h1 className={classes.coverTitle}>{cover.reportTitle}</h1>
          )}
          <div className={classes.coverSub}>{cover.reportPeriod}</div>
          <div className={classes.rcGrid}>
            {[
              ["Facility", cover.facilityName],
              ["Report period", cover.reportPeriod],
              ["Note entries", String(cover.noteCount)],
              ["Note types", cover.noteTypeNames.join(", ") || "None"],
              ["Clients", String(cover.clientCount)],
              ["Staff", String(cover.staffCount)],
              ["Generated by", `${cover.generatedByName} (${cover.generatedByRole})`],
              ["Generated on", cover.generatedOn],
            ].map(([k, v]) => (
              <div className={classes.rcRow} key={k}>
                <span className={classes.rcK}>{k}</span>
                <span className={classes.rcV}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.pdfFoot}>
          <span>{cover.facilityName} · Confidential clinical record</span>
          <span>Page 1 of {totalPages}</span>
        </div>
      </div>

      {/* ── Entry pages, 3 per page ─────────────────────────────────────── */}
      {pageChunks.map((chunk, pi) => (
        <div className={classes.page} key={pi}>
          <Letterhead />
          <div className={classes.reList}>{chunk.map(EntryBlock)}</div>
          <div className={classes.pdfFoot}>
            <span>{cover.facilityName} · Confidential clinical record</span>
            <span>
              Page {pi + 2} of {totalPages}
            </span>
          </div>
        </div>
      ))}

      {entries.length === 0 && (
        <Text align="center" color="dimmed" mt={20}>
          No note entries matched the selected filters.
        </Text>
      )}
    </Box>
  );
};

export default ReportPreviewPane;
